import { useState, useCallback, useContext, useMemo } from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { MAX_CONVERSIONS } from '@bingads-webui-campaign-react/bid-strategy-component';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { showBudgetBidCampaignNotifications } from '@bingads-webui-campaign/import-shared-utils';
import { LogTargetCpaCheckOnSave } from '@bingads-webui-campaign-react/target-cpa-warning-banner';

import { useReadCampaignPromise } from './use-read-campaign';
import { createOrUpdateCampaign } from '../utils/create-or-update-campaign';

export const useRepository = (isBudget: Boolean) => {
  const { scope } = useContext(ScopeContext) as any;
  const { campaignId } = scope.values;
  const instrumentation = useContext(InstrumentationContext) as any;
  const readCampaign = useReadCampaignPromise();
  const [initialCampaign, setInitialCampaign] = useState<any>({});

  const log = useMemo(() => {
    if (instrumentation && instrumentation.campaignOverviewEdit) {
      const scenario = instrumentation.campaignOverviewEdit.create();
      return isBudget ? scenario.budgetEdit.create() : scenario.bidStrategyEdit.create();
    }
    return {
      trace: _.noop,
      error: _.noop,
    };
  }, [instrumentation, isBudget]);
  const i18n = useContext(I18nContext) as any;
  const serverContext = useContext(ServerContext) as any;
  const currencyCode = serverContext?.CurrentAccount?.CurrencyCode;

  const localizeBudget = useCallback((budget, currency) => i18n.formatCurrency(
    Number(budget), currency.toUpperCase(),
    { withSymbol: false }
  ), [i18n]);

  const loadCampaign = useCallback(() => {
    if (!campaignId) {
      return Promise.resolve({
        initialBudget: null,
        bidStrategy: null,
        cpa: null,
      });
    }

    return readCampaign().then((campaign) => {
      log.trace(`Load campaign succeed: ${JSON.stringify(campaign)}`);
      setInitialCampaign(campaign);
      const campaignType = campaign?.CampaignType;
      const campaignGoal = campaign?.CampaignSettings?.[0]?.Goal;
      const budgetAmount = campaign?.Budget?.Amount;
      const bidStrategy = campaign?.BiddingScheme?.Type;
      const cpa = campaign?.BiddingScheme?.TargetCpa;
      const roas = campaign?.BiddingScheme?.TargetRoas;
      const language = _.first(campaign?.Languages);
      const isInitialAdditionalDetailsChecked = _.isNumber(cpa) || _.isNumber(roas);
      const deliveryStatus = campaign?.DeliveryStatus;
      const deliveryStatusDetails = campaign?.DeliveryStatusDetails;

      return {
        campaignType,
        campaignGoal,
        budgetAmount,
        bidStrategy,
        cpa,
        roas: (_.isNumber(roas) ? roas * 100 : null),
        language,
        isInitialAdditionalDetailsChecked,
        deliveryStatus,
        deliveryStatusDetails,
      };
    }).catch((error) => {
      log.error(`Load campaign fail: ${JSON.stringify(error)}`);

      return Promise.reject(error);
    });
  }, [log, campaignId, readCampaign]);

  const load = useCallback(() => loadCampaign().then((campaignData) => {
    const currency = currencyCode;

    return ({
      ...campaignData,
      currency,
      budgetAmount: localizeBudget(campaignData.budgetAmount, currency),
      isBudgetValid: true,
      isMarketingMetricValid: true,
    });
  }).catch((error) => {
    log.error(`Load campaign requests fail: ${JSON.stringify(error)}`);
    return Promise.reject(error);
  }), [loadCampaign, localizeBudget, log, currencyCode]);

  const save = ({ data }) => {
    const {
      campaignType,
      budgetAmount,
      bidStrategy,
      cpa,
      roas,
      isBudgetValid,
      isMarketingMetricValid,
    } = data || {};

    if (!isBudgetValid || !isMarketingMetricValid) {
      return Promise.reject(new Error(`use-repository - isBudget ${isBudget}: invalid data to save isBudgetValid: ${isBudgetValid} isMarketingMetricValid: ${isMarketingMetricValid}`));
    }

    let marketingMetric = {};
    if (campaignType === 'PerformanceMax') {
      marketingMetric = bidStrategy === MAX_CONVERSIONS.type
        ? { TargetCpa: cpa }
        : { TargetRoas: (_.isNumber(roas) ? (roas / 100).toFixed(4) : null) };
    }

    const updateCampaign = () => {
      const modelToSave = {
        Id: campaignId,
        BiddingScheme: {
          Type: bidStrategy,
          ...marketingMetric,
        },
        Budget: {
          Amount: i18n.parseDecimal(budgetAmount),
          Type: 'DailyStandard',
        },
      };

      return createOrUpdateCampaign(modelToSave).then((res) => {
        log.trace(`Update ${isBudget ? 'budget' : 'bid strategy'} succeed`);
        const { activity: BudgetBidCampaignImportNotificationActivity } = instrumentation.createScenarioAndActivity({
          scenarioName: 'BudgetBidCampaignImportNotification',
          activityName: 'PmaxCampaignStatusCard',
        });


        showBudgetBidCampaignNotifications({
          campaignItemList: [initialCampaign],
          oldBudget: initialCampaign?.Budget,
          newBudget: modelToSave.Budget,
          oldBidStrategy: initialCampaign?.BiddingScheme,
          newBidStrategy: modelToSave.BiddingScheme,
          activity: BudgetBidCampaignImportNotificationActivity,
        });
        return res;
      }).catch((error) => {
        log.error(`Update ${isBudget ? 'budget' : 'bid strategy'} fails: ${JSON.stringify(error)}`);
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ error });
      });
    };

    return updateCampaign().then(() => {
      log.trace(`Save ${isBudget ? 'budget' : 'bid strategy'} succeed`);
      if (!isBudget) {
        LogTargetCpaCheckOnSave(i18n, { dynamic: { TargetCpaValueCheck: true } }, budgetAmount, cpa, log, 'campaign-status-summary-card');
      }
      return data;
    }).catch((error) => {
      log.error(`Save ${isBudget ? 'budget' : 'bid strategy'} fails: ${JSON.stringify(error)}`);
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ error });
    });
  };

  return {
    load,
    save,
  };
};

export const useBudgetRepository = () => useRepository(true);
export const useBidStrategyRepository = () => useRepository(false);
